// Mapa de metadata derivada por columnId para alimentar los filtros (facets) del modal V2.
// NO se persiste — todo se calcula en frontend a partir del ID de columna y, para flex indicators,
// a partir de su substructure (FLEX_INDICATOR||metric||main||vs).

import {
    AGENCY_FLEX_INDICATOR,
    FLEX_INDICATOR_COLUMN_TO_DATA,
    TODAY_CLICKS_COLUMN, TODAY_IMPRESSIONS_COLUMN, TODAY_COST_COLUMN, TODAY_CONVERSIONS_COLUMN,
    TODAY_CPC_COLUMN, TODAY_CPA_COLUMN, TODAY_CTR_COLUMN, TODAY_CPM_COLUMN,
    CURRENT_PERIOD_CPA_COLUMN,
    CURRENT_PERIOD_CPA_VS_LAST_PERIOD_CPA_COLUMN, CURRENT_PERIOD_CPA_VS_LAST_THREE_PERIODS_CPA_COLUMN,
    CURRENT_PERIOD_CPA_VS_LAST_SIX_PERIODS_CPA_COLUMN,
    YESTERDAY_CPA_VS_LAST_PERIOD_COLUMN, YESTERDAY_CPA_VS_LAST_THREE_PERIODS_CPA_COLUMN,
    YESTERDAY_CPA_VS_LAST_SIX_PERIODS_CPA_COLUMN,
    FULL_PERIOD_BUDGET_PROPORTIONAL_COLUMN, FULL_PERIOD_COST_VS_PROJECTED_COLUMN, FULL_PERIOD_ODS_COLUMN, YESTERDAY_EXPENSES_COLUMN,
    LAST_7_DAYS_AVERAGE_EXPENSES_COLUMN, FULL_PERIOD_ODS_VS_YESTERDAY_COLUMN,
    FULL_PERIOD_ODS_VS_LAST_7_DAYS_COLUMN, FULL_PERIOD_ODS_VS_PLATFORM_BUDGET_COLUMN, FULL_PERIOD_CONVERSIONS_COLUMN,
    PURCHASES_VALUE_COLUMN, LAST_PERIOD_PURCHASES_VALUE_COLUMN, ROAS_COLUMN, ACOS_COLUMN,
    FULL_PERIOD_ROAS_VS_LAST_MONTH_COLUMN, FULL_PERIOD_ACOS_VS_LAST_MONTH_COLUMN,
    CURRENT_PERIOD_ALLOCATED_BUDGET_COLUMN, CURRENT_PERIOD_PERIOD_DATES_COLUMN,
    CURRENT_PERIOD_ROLLOVER_BALANCE_COLUMN, CURRENT_PERIOD_TOTAL_BUDGET_COLUMN,
    CURRENT_PERIOD_COST_COLUMN,
    LAST_PERIOD_ALLOCATED_BUDGET_COLUMN, LAST_PERIOD_ROLLOVER_BALANCE_COLUMN,
    LAST_PERIOD_TOTAL_BUDGET_COLUMN, LAST_PERIOD_COST_COLUMN,
    BUSINESS_UNIT_COLUMN, LABELS_COLUMN, CURRENCY_COLUMN,
    GOAL_1_COLUMN, GOAL_1_INFO_COLUMN, GOAL_2_COLUMN, GOAL_2_INFO_COLUMN,
    AVAILABLE_FUNDS_COLUMN, DAYS_TILL_FUNDS_ARE_OUT_COLUMN, AVAILABLE_FUNDS_LAST_UPLOAD_ON_COLUMN,
    COMMENTS_COLUMN, PLATFORM_BUDGET_COLUMN, CAMPAIGN_STATUS_COLUMN,
    CURRENT_PERIOD_TIME_COMPLETION_COLUMN, CURRENT_PERIOD_AMOUNT_COMPLETION_COLUMN,
    CURRENT_PERIOD_COST_VS_TIME_COLUMN,
} from "./columns";

// Channels
export const CHANNEL_ALL = 'ALL';
export const CHANNEL_PAID_MEDIA = 'PAID_MEDIA';
export const CHANNEL_GA4 = 'GA4';
export const CHANNEL_ECOMMERCE = 'ECOMMERCE';
export const CHANNEL_BU = 'BU';

// Categories
export const CATEGORY_GOALS = 'GOALS';
export const CATEGORY_BUDGET = 'BUDGET';
export const CATEGORY_PERFORMANCE = 'PERFORMANCE';
export const CATEGORY_CONVERSIONS = 'CONVERSIONS';
export const CATEGORY_FUNDS = 'FUNDS';
export const CATEGORY_ATTRIBUTES = 'ATTRIBUTES';
export const CATEGORY_STATUS = 'STATUS';
// Categoría especial para AgencyCustomMetric — facet "Mis métricas" en el modal V2.
export const CATEGORY_CUSTOM = 'CUSTOM';
// Desprendimientos de Performance (pedido Pablo): video y social/engagement como categorías propias.
export const CATEGORY_VIDEO = 'VIDEO';
export const CATEGORY_SOCIAL = 'SOCIAL';

// Clasificación canónica de un flex indicator por su metric_name. Única fuente de verdad usada
// por buildStackedRows (panel izquierdo, row.category) Y por deriveFacetsForFlexIndicator
// (panel derecho, getColumnFacets().category) — así ambos paneles coinciden.
// Precedencia: conversiones → video → social → performance.
// OJO: 'share' NO va en social (colisiona con "Impression Share" de Google → quedaría en Performance).
export function categorizeFlexMetric(metricName) {
    const n = (metricName || '').toLowerCase();
    if (/(roas|acos|cpa|purchase|conversion|order|revenue|sale)/.test(n)) return CATEGORY_CONVERSIONS;
    if (/(video|cpv|quartile|vtr)/.test(n)) return CATEGORY_VIDEO;
    if (/(like|comment|reaction|reach|frequency|follower|profile|conversation|message|messaging|engagement)/.test(n)) return CATEGORY_SOCIAL;
    return CATEGORY_PERFORMANCE;
}

// Grupo de canal (logo/color de marca) de una métrica, derivado del metric_name.
// Devuelve una key de CHANNEL_VISUAL (GOOGLE/META/TIKTOK/LINKEDIN/DV360/GA4/GSC/ECOMMERCE) o `fallback`.
export function channelGroupForMetric(metricName, fallback = null) {
    const n = (metricName || '').toLowerCase();
    if (!n) return fallback;
    if (n.includes('ga4')) return 'GA4';
    if (n.includes('ecommerce')) return 'ECOMMERCE';
    if (n.startsWith('meta_') || n.includes('(meta)')) return 'META';
    if (n.includes('tiktok')) return 'TIKTOK';
    if (n.includes('linkedin')) return 'LINKEDIN';
    if (n.includes('dv360')) return 'DV360';
    // GSC antes que Google: "Google Search Console" contiene "google" y, de chequear Google
    // primero, le pondríamos el logo equivocado.
    if (n.includes('gsc') || n.includes('search console')) return 'GSC';
    if (n.includes('google')) return 'GOOGLE';
    return fallback;
}

// Igual que channelGroupForMetric pero a partir de un columnId (flex o fijo). Para columnas fijas
// devuelve el canal del facet (GA4/ECOMMERCE) o null para PAID_MEDIA/BU (no llevan badge de canal).
export function channelGroupForColumn(columnId) {
    if (!columnId) return null;
    if (columnId.includes(AGENCY_FLEX_INDICATOR)) {
        const parts = FLEX_INDICATOR_COLUMN_TO_DATA(columnId);
        return channelGroupForMetric(parts && parts.metricName, null);
    }
    const facets = getColumnFacets(columnId);
    if (facets.channel === CHANNEL_GA4) return 'GA4';
    if (facets.channel === CHANNEL_ECOMMERCE) return 'ECOMMERCE';
    return null;
}

// Period buckets (más gruesos que los 19 períodos canónicos)
export const PB_TODAY = 'TODAY';
export const PB_YESTERDAY = 'YESTERDAY';
export const PB_CURRENT_PERIOD = 'CURRENT_PERIOD';
export const PB_LAST_PERIOD = 'LAST_PERIOD';
export const PB_LAST_7D = 'LAST_7D';
export const PB_LAST_30D = 'LAST_30D';
export const PB_STATIC = 'STATIC';

// Unit types
export const UNIT_CURRENCY = 'CURRENCY';
export const UNIT_PERCENT = 'PERCENT';
export const UNIT_COUNT = 'COUNT';
export const UNIT_RATIO = 'RATIO';
export const UNIT_TEXT = 'TEXT';
export const UNIT_DATE = 'DATE';

// Metric families
export const MF_COST = 'COST';
export const MF_CPA = 'CPA';
export const MF_ROAS = 'ROAS';
export const MF_ACOS = 'ACOS';
export const MF_CTR = 'CTR';
export const MF_CPC = 'CPC';
export const MF_CPM = 'CPM';
export const MF_CLICKS = 'CLICKS';
export const MF_IMPRESSIONS = 'IMPRESSIONS';
export const MF_CONVERSIONS = 'CONVERSIONS';
export const MF_PURCHASES = 'PURCHASES';
export const MF_BUDGET = 'BUDGET';
export const MF_FUNDS = 'FUNDS';
export const MF_GOAL = 'GOAL';
export const MF_STATUS = 'STATUS';
export const MF_ATTRIBUTE = 'ATTRIBUTE';

const meta = (channel, category, metricFamily, periodBucket, hasComparison, unitType, iconName, searchTokens = [], requiresIntegration = null) => ({
    channel, category, metricFamily, periodBucket, hasComparison, unitType, iconName, searchTokens, requiresIntegration,
});

// Mapeo explícito de las ~60 columnas fijas. Si una columna no está acá, getColumnFacets devuelve un fallback inferido.
const FIXED_COLUMNS_METADATA = {
    // Atributos / Línea de control
    [BUSINESS_UNIT_COLUMN]: meta(CHANNEL_BU, CATEGORY_ATTRIBUTES, MF_ATTRIBUTE, PB_STATIC, false, UNIT_TEXT, 'building'),
    [LABELS_COLUMN]: meta(CHANNEL_BU, CATEGORY_ATTRIBUTES, MF_ATTRIBUTE, PB_STATIC, false, UNIT_TEXT, 'tags'),
    [CURRENCY_COLUMN]: meta(CHANNEL_BU, CATEGORY_ATTRIBUTES, MF_ATTRIBUTE, PB_STATIC, false, UNIT_TEXT, 'dollar-sign'),
    [CURRENT_PERIOD_PERIOD_DATES_COLUMN]: meta(CHANNEL_BU, CATEGORY_ATTRIBUTES, MF_ATTRIBUTE, PB_CURRENT_PERIOD, false, UNIT_DATE, 'calendar-alt'),
    [COMMENTS_COLUMN]: meta(CHANNEL_BU, CATEGORY_ATTRIBUTES, MF_ATTRIBUTE, PB_STATIC, false, UNIT_TEXT, 'comment-alt'),
    [CAMPAIGN_STATUS_COLUMN]: meta(CHANNEL_PAID_MEDIA, CATEGORY_STATUS, MF_STATUS, PB_STATIC, false, UNIT_TEXT, 'circle'),

    // Saldos
    [AVAILABLE_FUNDS_COLUMN]: meta(CHANNEL_BU, CATEGORY_FUNDS, MF_FUNDS, PB_STATIC, false, UNIT_CURRENCY, 'wallet'),
    [DAYS_TILL_FUNDS_ARE_OUT_COLUMN]: meta(CHANNEL_BU, CATEGORY_FUNDS, MF_FUNDS, PB_STATIC, false, UNIT_COUNT, 'hourglass-half'),
    [AVAILABLE_FUNDS_LAST_UPLOAD_ON_COLUMN]: meta(CHANNEL_BU, CATEGORY_FUNDS, MF_FUNDS, PB_STATIC, false, UNIT_DATE, 'pencil-alt'),

    // Goals
    [GOAL_1_INFO_COLUMN]: meta(CHANNEL_BU, CATEGORY_GOALS, MF_GOAL, PB_STATIC, false, UNIT_TEXT, 'bullseye'),
    [GOAL_1_COLUMN]: meta(CHANNEL_BU, CATEGORY_GOALS, MF_GOAL, PB_CURRENT_PERIOD, false, UNIT_PERCENT, 'crosshairs'),
    [GOAL_2_INFO_COLUMN]: meta(CHANNEL_BU, CATEGORY_GOALS, MF_GOAL, PB_STATIC, false, UNIT_TEXT, 'bullseye'),
    [GOAL_2_COLUMN]: meta(CHANNEL_BU, CATEGORY_GOALS, MF_GOAL, PB_CURRENT_PERIOD, false, UNIT_PERCENT, 'crosshairs'),

    // Hoy (Performance Paid Media)
    [TODAY_CLICKS_COLUMN]: meta(CHANNEL_PAID_MEDIA, CATEGORY_PERFORMANCE, MF_CLICKS, PB_TODAY, false, UNIT_COUNT, 'mouse-pointer', ['clicks']),
    [TODAY_IMPRESSIONS_COLUMN]: meta(CHANNEL_PAID_MEDIA, CATEGORY_PERFORMANCE, MF_IMPRESSIONS, PB_TODAY, false, UNIT_COUNT, 'eye', ['impresiones']),
    [TODAY_COST_COLUMN]: meta(CHANNEL_PAID_MEDIA, CATEGORY_PERFORMANCE, MF_COST, PB_TODAY, false, UNIT_CURRENCY, 'dollar-sign', ['costo','spend']),
    [TODAY_CONVERSIONS_COLUMN]: meta(CHANNEL_PAID_MEDIA, CATEGORY_CONVERSIONS, MF_CONVERSIONS, PB_TODAY, false, UNIT_COUNT, 'check-double', ['conversiones']),
    [TODAY_CPC_COLUMN]: meta(CHANNEL_PAID_MEDIA, CATEGORY_PERFORMANCE, MF_CPC, PB_TODAY, false, UNIT_CURRENCY, 'mouse-pointer', ['cpc','costo por click']),
    [TODAY_CPA_COLUMN]: meta(CHANNEL_PAID_MEDIA, CATEGORY_CONVERSIONS, MF_CPA, PB_TODAY, false, UNIT_CURRENCY, 'bullseye', ['cpa','costo por adquisición']),
    [TODAY_CTR_COLUMN]: meta(CHANNEL_PAID_MEDIA, CATEGORY_PERFORMANCE, MF_CTR, PB_TODAY, false, UNIT_PERCENT, 'percent', ['ctr','click through rate']),
    [TODAY_CPM_COLUMN]: meta(CHANNEL_PAID_MEDIA, CATEGORY_PERFORMANCE, MF_CPM, PB_TODAY, false, UNIT_CURRENCY, 'eye', ['cpm']),

    // Presupuesto (Este período)
    [PLATFORM_BUDGET_COLUMN]: meta(CHANNEL_PAID_MEDIA, CATEGORY_BUDGET, MF_BUDGET, PB_STATIC, false, UNIT_CURRENCY, 'sliders-h'),
    [CURRENT_PERIOD_ALLOCATED_BUDGET_COLUMN]: meta(CHANNEL_BU, CATEGORY_BUDGET, MF_BUDGET, PB_CURRENT_PERIOD, false, UNIT_CURRENCY, 'piggy-bank'),
    [CURRENT_PERIOD_TOTAL_BUDGET_COLUMN]: meta(CHANNEL_BU, CATEGORY_BUDGET, MF_BUDGET, PB_CURRENT_PERIOD, false, UNIT_CURRENCY, 'piggy-bank'),
    [CURRENT_PERIOD_ROLLOVER_BALANCE_COLUMN]: meta(CHANNEL_BU, CATEGORY_BUDGET, MF_BUDGET, PB_CURRENT_PERIOD, false, UNIT_CURRENCY, 'exchange-alt'),
    [CURRENT_PERIOD_COST_COLUMN]: meta(CHANNEL_PAID_MEDIA, CATEGORY_PERFORMANCE, MF_COST, PB_CURRENT_PERIOD, false, UNIT_CURRENCY, 'dollar-sign'),
    [FULL_PERIOD_BUDGET_PROPORTIONAL_COLUMN]: meta(CHANNEL_BU, CATEGORY_BUDGET, MF_BUDGET, PB_CURRENT_PERIOD, true, UNIT_PERCENT, 'chart-line'),
    [FULL_PERIOD_COST_VS_PROJECTED_COLUMN]: meta(CHANNEL_BU, CATEGORY_BUDGET, MF_BUDGET, PB_CURRENT_PERIOD, true, UNIT_PERCENT, 'chart-line'),
    [FULL_PERIOD_ODS_COLUMN]: meta(CHANNEL_BU, CATEGORY_BUDGET, MF_BUDGET, PB_CURRENT_PERIOD, false, UNIT_CURRENCY, 'compass'),
    [YESTERDAY_EXPENSES_COLUMN]: meta(CHANNEL_PAID_MEDIA, CATEGORY_PERFORMANCE, MF_COST, PB_YESTERDAY, false, UNIT_CURRENCY, 'dollar-sign'),
    [LAST_7_DAYS_AVERAGE_EXPENSES_COLUMN]: meta(CHANNEL_PAID_MEDIA, CATEGORY_PERFORMANCE, MF_COST, PB_LAST_7D, false, UNIT_CURRENCY, 'chart-line'),
    [FULL_PERIOD_ODS_VS_YESTERDAY_COLUMN]: meta(CHANNEL_BU, CATEGORY_BUDGET, MF_BUDGET, PB_CURRENT_PERIOD, true, UNIT_PERCENT, 'balance-scale'),
    [FULL_PERIOD_ODS_VS_LAST_7_DAYS_COLUMN]: meta(CHANNEL_BU, CATEGORY_BUDGET, MF_BUDGET, PB_CURRENT_PERIOD, true, UNIT_PERCENT, 'balance-scale'),
    [FULL_PERIOD_ODS_VS_PLATFORM_BUDGET_COLUMN]: meta(CHANNEL_BU, CATEGORY_BUDGET, MF_BUDGET, PB_CURRENT_PERIOD, true, UNIT_PERCENT, 'sliders-h'),
    [CURRENT_PERIOD_TIME_COMPLETION_COLUMN]: meta(CHANNEL_BU, CATEGORY_BUDGET, MF_BUDGET, PB_CURRENT_PERIOD, false, UNIT_PERCENT, 'clock'),
    [CURRENT_PERIOD_AMOUNT_COMPLETION_COLUMN]: meta(CHANNEL_BU, CATEGORY_BUDGET, MF_BUDGET, PB_CURRENT_PERIOD, false, UNIT_PERCENT, 'tachometer-alt'),
    [CURRENT_PERIOD_COST_VS_TIME_COLUMN]: meta(CHANNEL_BU, CATEGORY_BUDGET, MF_BUDGET, PB_CURRENT_PERIOD, true, UNIT_PERCENT, 'balance-scale'),

    // Last period budget (comparativa contra mes anterior)
    [LAST_PERIOD_ALLOCATED_BUDGET_COLUMN]: meta(CHANNEL_BU, CATEGORY_BUDGET, MF_BUDGET, PB_LAST_PERIOD, false, UNIT_CURRENCY, 'piggy-bank'),
    [LAST_PERIOD_ROLLOVER_BALANCE_COLUMN]: meta(CHANNEL_BU, CATEGORY_BUDGET, MF_BUDGET, PB_LAST_PERIOD, false, UNIT_CURRENCY, 'exchange-alt'),
    [LAST_PERIOD_TOTAL_BUDGET_COLUMN]: meta(CHANNEL_BU, CATEGORY_BUDGET, MF_BUDGET, PB_LAST_PERIOD, false, UNIT_CURRENCY, 'piggy-bank'),
    [LAST_PERIOD_COST_COLUMN]: meta(CHANNEL_PAID_MEDIA, CATEGORY_PERFORMANCE, MF_COST, PB_LAST_PERIOD, false, UNIT_CURRENCY, 'dollar-sign'),

    // Conversiones (Este período)
    [FULL_PERIOD_CONVERSIONS_COLUMN]: meta(CHANNEL_PAID_MEDIA, CATEGORY_CONVERSIONS, MF_CONVERSIONS, PB_CURRENT_PERIOD, false, UNIT_COUNT, 'check-double'),
    [PURCHASES_VALUE_COLUMN]: meta(CHANNEL_PAID_MEDIA, CATEGORY_CONVERSIONS, MF_PURCHASES, PB_CURRENT_PERIOD, false, UNIT_CURRENCY, 'shopping-cart'),
    [LAST_PERIOD_PURCHASES_VALUE_COLUMN]: meta(CHANNEL_PAID_MEDIA, CATEGORY_CONVERSIONS, MF_PURCHASES, PB_LAST_PERIOD, false, UNIT_CURRENCY, 'shopping-cart'),
    [ROAS_COLUMN]: meta(CHANNEL_PAID_MEDIA, CATEGORY_CONVERSIONS, MF_ROAS, PB_CURRENT_PERIOD, false, UNIT_RATIO, 'chart-bar', ['roas']),
    [ACOS_COLUMN]: meta(CHANNEL_PAID_MEDIA, CATEGORY_CONVERSIONS, MF_ACOS, PB_CURRENT_PERIOD, false, UNIT_PERCENT, 'chart-pie', ['acos']),
    [FULL_PERIOD_ROAS_VS_LAST_MONTH_COLUMN]: meta(CHANNEL_PAID_MEDIA, CATEGORY_CONVERSIONS, MF_ROAS, PB_CURRENT_PERIOD, true, UNIT_RATIO, 'chart-bar'),
    [FULL_PERIOD_ACOS_VS_LAST_MONTH_COLUMN]: meta(CHANNEL_PAID_MEDIA, CATEGORY_CONVERSIONS, MF_ACOS, PB_CURRENT_PERIOD, true, UNIT_PERCENT, 'chart-pie'),

    // CPA en sus 7 sabores
    [CURRENT_PERIOD_CPA_COLUMN]: meta(CHANNEL_PAID_MEDIA, CATEGORY_CONVERSIONS, MF_CPA, PB_CURRENT_PERIOD, false, UNIT_CURRENCY, 'bullseye', ['cpa']),
    [CURRENT_PERIOD_CPA_VS_LAST_PERIOD_CPA_COLUMN]: meta(CHANNEL_PAID_MEDIA, CATEGORY_CONVERSIONS, MF_CPA, PB_CURRENT_PERIOD, true, UNIT_CURRENCY, 'bullseye'),
    [CURRENT_PERIOD_CPA_VS_LAST_THREE_PERIODS_CPA_COLUMN]: meta(CHANNEL_PAID_MEDIA, CATEGORY_CONVERSIONS, MF_CPA, PB_CURRENT_PERIOD, true, UNIT_CURRENCY, 'bullseye'),
    [CURRENT_PERIOD_CPA_VS_LAST_SIX_PERIODS_CPA_COLUMN]: meta(CHANNEL_PAID_MEDIA, CATEGORY_CONVERSIONS, MF_CPA, PB_CURRENT_PERIOD, true, UNIT_CURRENCY, 'bullseye'),
    [YESTERDAY_CPA_VS_LAST_PERIOD_COLUMN]: meta(CHANNEL_PAID_MEDIA, CATEGORY_CONVERSIONS, MF_CPA, PB_YESTERDAY, true, UNIT_CURRENCY, 'bullseye'),
    [YESTERDAY_CPA_VS_LAST_THREE_PERIODS_CPA_COLUMN]: meta(CHANNEL_PAID_MEDIA, CATEGORY_CONVERSIONS, MF_CPA, PB_YESTERDAY, true, UNIT_CURRENCY, 'bullseye'),
    [YESTERDAY_CPA_VS_LAST_SIX_PERIODS_CPA_COLUMN]: meta(CHANNEL_PAID_MEDIA, CATEGORY_CONVERSIONS, MF_CPA, PB_YESTERDAY, true, UNIT_CURRENCY, 'bullseye'),
};

// Fallback derivativo para columnas no listadas (incluyendo TODAS las flex indicators).
function deriveFacetsForFlexIndicator(columnId) {
    const parts = FLEX_INDICATOR_COLUMN_TO_DATA(columnId);
    if (!parts) return null;

    const {metricName, vsPeriod} = parts;
    const lowerName = (metricName || '').toLowerCase();

    // Comparativas sobre métricas personalizadas (CUSTOM:<id>) → grupo "Métricas personalizadas".
    if (metricName && metricName.startsWith('CUSTOM:')) {
        return {
            channel: CHANNEL_BU,
            category: CATEGORY_CUSTOM,
            metricFamily: null,
            periodBucket: PB_CURRENT_PERIOD,
            hasComparison: vsPeriod && vsPeriod !== 'NO_COMPARATION',
            unitType: UNIT_COUNT,
            iconName: 'wand-magic-sparkles',
            searchTokens: ['custom'],
            requiresIntegration: null,
        };
    }

    let channel = CHANNEL_PAID_MEDIA;
    let requiresIntegration = null;
    if (metricName && metricName.includes('GA4')) {
        channel = CHANNEL_GA4;
        requiresIntegration = 'GA4';
    } else if (lowerName.includes('ecommerce')) {
        channel = CHANNEL_ECOMMERCE;
        requiresIntegration = 'ECOMMERCE';
    }

    // Inferir métrica family por substring del metricName
    let metricFamily = null;
    let unitType = UNIT_COUNT;
    let category = CATEGORY_PERFORMANCE;
    let iconName = 'chart-line';
    if (lowerName.includes('roas')) { metricFamily = MF_ROAS; unitType = UNIT_RATIO; category = CATEGORY_CONVERSIONS; iconName = 'chart-bar'; }
    else if (lowerName.includes('acos')) { metricFamily = MF_ACOS; unitType = UNIT_PERCENT; category = CATEGORY_CONVERSIONS; iconName = 'chart-pie'; }
    else if (lowerName.includes('cpa')) { metricFamily = MF_CPA; unitType = UNIT_CURRENCY; category = CATEGORY_CONVERSIONS; iconName = 'bullseye'; }
    else if (lowerName.includes('cpc')) { metricFamily = MF_CPC; unitType = UNIT_CURRENCY; iconName = 'mouse-pointer'; }
    else if (lowerName.includes('cpm')) { metricFamily = MF_CPM; unitType = UNIT_CURRENCY; iconName = 'eye'; }
    else if (lowerName.includes('ctr')) { metricFamily = MF_CTR; unitType = UNIT_PERCENT; iconName = 'percent'; }
    else if (lowerName.includes('conversion')) { metricFamily = MF_CONVERSIONS; unitType = UNIT_COUNT; category = CATEGORY_CONVERSIONS; iconName = 'check-double'; }
    else if (lowerName.includes('click')) { metricFamily = MF_CLICKS; unitType = UNIT_COUNT; iconName = 'mouse-pointer'; }
    else if (lowerName.includes('impression') || lowerName.includes('reach')) { metricFamily = MF_IMPRESSIONS; unitType = UNIT_COUNT; iconName = 'eye'; }
    else if (lowerName.includes('cost') || lowerName === 'cost') { metricFamily = MF_COST; unitType = UNIT_CURRENCY; iconName = 'dollar-sign'; }
    else if (lowerName.includes('purchase') || lowerName.includes('revenue')) { metricFamily = MF_PURCHASES; unitType = UNIT_CURRENCY; category = CATEGORY_CONVERSIONS; iconName = 'shopping-cart'; }
    else if (lowerName.includes('session')) { metricFamily = MF_IMPRESSIONS; unitType = UNIT_COUNT; iconName = 'globe'; }
    else if (lowerName.includes('order')) { metricFamily = MF_CONVERSIONS; unitType = UNIT_COUNT; category = CATEGORY_CONVERSIONS; iconName = 'shopping-bag'; }

    // La categoría final la decide la clasificación canónica (cubre VIDEO/SOCIAL además de
    // CONVERSIONS/PERFORMANCE), para no divergir del panel izquierdo.
    category = categorizeFlexMetric(metricName);

    return {
        channel,
        category,
        metricFamily,
        periodBucket: PB_CURRENT_PERIOD,  // las flex indicators más usadas anclan en current
        hasComparison: vsPeriod && vsPeriod !== 'NO_COMPARATION',
        unitType,
        iconName,
        searchTokens: [lowerName],
        requiresIntegration,
    };
}

const FALLBACK_FACETS = meta(CHANNEL_PAID_MEDIA, CATEGORY_PERFORMANCE, null, PB_CURRENT_PERIOD, false, UNIT_COUNT, 'columns');

export function getColumnFacets(columnId) {
    if (!columnId) return FALLBACK_FACETS;
    if (columnId.includes(AGENCY_FLEX_INDICATOR)) {
        return deriveFacetsForFlexIndicator(columnId) || FALLBACK_FACETS;
    }
    return FIXED_COLUMNS_METADATA[columnId] || FALLBACK_FACETS;
}

// Helpers para los filtros del modal
export function matchesFacetFilter(columnId, filter) {
    const facets = getColumnFacets(columnId);
    if (filter.channel && filter.channel !== CHANNEL_ALL && facets.channel !== filter.channel && facets.channel !== CHANNEL_BU && facets.channel !== CHANNEL_ALL) {
        return false;
    }
    if (filter.category && facets.category !== filter.category) return false;
    if (filter.periodBucket && facets.periodBucket !== filter.periodBucket) return false;
    if (filter.hasComparison !== undefined && filter.hasComparison !== null && facets.hasComparison !== filter.hasComparison) return false;
    if (filter.unitType && facets.unitType !== filter.unitType) return false;
    return true;
}
